import { useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import {
    Button,
    Chip,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Spinner,
    Progress
} from "@heroui/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { Folder as FolderIcon } from "assets/icons";

// Professional premium loader component
const PremiumLoader = () => {
    return (
        <div className="flex flex-col items-center justify-center py-10 space-y-6">
            <div className="w-16 h-16 relative">
                <Spinner
                    size="lg"
                    classNames={{
                        circle1: "border-neutral-primary",
                        circle2: "border-neutral-primary/70"
                    }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <FolderIcon className="h-6 w-6 text-neutral-primary" />
                </div>
            </div>

            <div className="w-64 space-y-3">
                <div className="text-center text-neutral-primary font-medium">
                    Loading folder structure
                </div>
                <Progress
                    size="sm"
                    isIndeterminate
                    aria-label="Loading..."
                    classNames={{
                        indicator: "bg-neutral-primary"
                    }}
                />
            </div>
        </div>
    );
};

interface FolderStructureProps {
    folder: any;
    level?: number;
    nonLinkedLabels: any[];
    refetchFolderStructure: () => void;
    clearCache: () => void;
}

// Regular folder structure component (without virtualization)
const FolderStructure = ({
    folder,
    level = 0,
    nonLinkedLabels,
    refetchFolderStructure,
    clearCache
}: FolderStructureProps) => {
    const { session } = useSession();
    const [isExpanded, setIsExpanded] = useState(level < 2); // Auto-expand first two levels

    if (!folder) return null;

    // Ensure allowedFileLabels is an array
    const allowedFileLabels = folder.allowedFileLabels || [];
    const children = folder.children || [];

    const handleAddFileLabel = async (labelId: string) => {
        try {
            // Using the original API endpoint format
            const folderName = folder.name;
            const parentId = folder.parent?.id || 0;
            
            const response = await fetchWithAuth(
                `${API_URL}/file/folder/${folderName}/label/${labelId}/${parentId}`,
                { method: "POST" },
                session
            );

            if (response.ok) {
                toast.success("Bestandstype toegevoegd aan map");
                clearCache();
            } else {
                toast.error("Fout bij toevoegen van bestandstype");
            }
        } catch (error) {
            console.error("Error adding file label:", error);
            toast.error("Fout bij toevoegen van bestandstype");
        }
    };

    const handleRemoveFileLabel = async (folderName: string, labelName: string, parentId: number) => {
        try {
            // Using the original API endpoint format for removal
            const response = await fetchWithAuth(
                `${API_URL}/file/folder/${folderName}/label/${labelName}/${parentId || 0}`,
                { method: "DELETE" },
                session
            );

            if (response.ok) {
                toast.success("Bestandstype verwijderd van map");
                clearCache();
            } else {
                toast.error("Fout bij verwijderen van bestandstype");
            }
        } catch (error) {
            console.error("Error removing file label:", error);
            toast.error("Fout bij verwijderen van bestandstype");
        }
    };

    return (
        <section className="flex flex-col gap-2.5">
            <section
                className="flex flex-col gap-2"
                style={{ marginLeft: `${level * 20}px` }}
            >
                <header className="flex items-center justify-between">
                    <div
                        className="flex items-center gap-1.5 cursor-pointer"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        <FolderIcon className="h-[18px] w-[18px] text-neutral-primary" />
                        <span className="font-medium">{folder.name}</span>
                        <span className="text-sm text-gray-500">
                            {isExpanded ? "▼" : "►"}
                        </span>
                    </div>

                    {nonLinkedLabels && nonLinkedLabels.length > 0 && (
                        <Dropdown>
                            <DropdownTrigger>
                                <Button
                                    size="sm"
                                    variant="light"
                                    className="text-xs px-2 py-1 h-auto bg-neutral-50 border border-neutral-200 text-neutral-primary"
                                >
                                    Add Label
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                                items={nonLinkedLabels}
                                aria-label="Add file label"
                                className="max-h-[300px] overflow-y-auto"
                            >
                                {(item) => (
                                    <DropdownItem
                                        key={item.id}
                                        onClick={() => handleAddFileLabel(item.id)}
                                    >
                                        {item.label}
                                    </DropdownItem>
                                )}
                            </DropdownMenu>
                        </Dropdown>
                    )}
                </header>

                <div className="flex flex-wrap gap-1.5">
                    {allowedFileLabels.map((label: any) => (
                        <Chip
                            key={typeof label === 'string' ? label : label.id || label.label}
                            onClose={() => handleRemoveFileLabel(
                                folder.name,
                                typeof label === 'string' ? label : label.label,
                                folder.parent?.id
                            )}
                            classNames={{
                                base: "flex gap-1 h-fit min-w-fit rounded-full border border-neutral-primary px-2 bg-white text-neutral-primary",
                                content: "font-medium p-0",
                                closeButton: "text-neutral-primary"
                            }}
                        >
                            {typeof label === 'string' ? label : label.label}
                        </Chip>
                    ))}
                </div>
            </section>

            {isExpanded && children.length > 0 && (
                <div className="ml-4">
                    {children.map((subFolder: any) => (
                        <FolderStructure
                            key={subFolder.id}
                            folder={subFolder}
                            level={level + 1}
                            nonLinkedLabels={nonLinkedLabels}
                            refetchFolderStructure={refetchFolderStructure}
                            clearCache={clearCache}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

// Export both as named export and default export
export const Sitemap = () => {
    const { session } = useSession();
    const [folderData, setFolderData] = useState(null);
    const [nonLinkedLabels, setNonLinkedLabels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    
    const clearCache = async () => {
        try {
            await fetchWithAuth(`${API_URL}/file/clear-folder-cache`, { method: "POST" }, session);
            toast.success("Cache geleegd, structuur wordt opnieuw geladen");
            refetchFolderStructure();
        } catch (error) {
            console.error("Error clearing cache:", error);
            toast.error("Fout bij het legen van de cache");
        }
    };

    const refetchFolderStructure = async () => {
        setIsLoading(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/file/architecture?rootFolder=building`,
                { method: "GET" },
                session
            );
            const data = await response.json();
            setFolderData(data);
            await refetchNonLinkedLabels();
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching folder architecture:", error);
            toast.error("Failed to refresh folder structure");
            setIsLoading(false);
        }
    };

    const refetchNonLinkedLabels = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/file/non-linked-labels`,
                { method: "GET" },
                session
            );
            const data = await response.json();
            setNonLinkedLabels(data);
        } catch (error) {
            console.error("Error fetching non-linked labels:", error);
            toast.error("Failed to refresh non-linked labels");
        }
    };

    useEffect(() => {
        refetchFolderStructure();
    }, []);

    return (
        <>
            <p className="mb-4 text-neutral-primary">
                <strong>
                    Op deze pagina kan je bestandstypes toevoegen aan onze mappenstructuur in SharePoint.
                    Indien een bestandstype gekoppeld wordt aan een map zal dit bestand, wanneer het door
                    een gebruiker op de Yallyhub geüpload wordt, in deze map terechtkomen.
                </strong>
            </p>

            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                {isLoading ? (
                    <PremiumLoader />
                ) : (
                    folderData ? (
                        <div className="max-w-4xl">
                            <FolderStructure
                                folder={folderData}
                                nonLinkedLabels={nonLinkedLabels}
                                refetchFolderStructure={refetchFolderStructure}
                                clearCache={clearCache}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <p>Geen mappenstructuur gevonden.</p>
                            <Button
                                className="mt-4"
                                color="primary"
                                onClick={refetchFolderStructure}
                            >
                                Probeer opnieuw
                            </Button>
                        </div>
                    )
                )}
            </div>

            <div className="mt-4 flex justify-end">
                <Button
                    color="primary"
                    onClick={clearCache}
                >
                    Ververs Mappenstructuur
                </Button>
            </div>
        </>
    );
};

// Also provide as default export for backward compatibility
export default Sitemap;
